import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';

import type {
  SanityImageAsset,
  SanityImageCrop,
  SanityImageHotspot,
  SanityReference,
} from 'sanity-codegen';

export interface TestimonialCardProps {
  logo: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
    crop?: SanityImageCrop;
    hotspot?: SanityImageHotspot;
  };
  company: string;
  _type: 'singleQuote';
  audience?: 'employer' | 'individual';
  quote: string;
  author?: string;
  role?: string;
}
const TestimonialCard = ({
  logo,
  company,
  audience,
  quote,
  author,
  role,
}: TestimonialCardProps) => {
  return (
    <SingleTestimonial>
      <Audience size="body-md">{audience}</Audience>
      <LogoWrapper>
        <Image {...logo} sizes="100px" />
      </LogoWrapper>
      <Quote size="body-md">{quote}</Quote>
      <Author size="body-sm">{author}</Author>
      <RoleCompany size="body-sm">
        {role && `${role} at`} {company}
      </RoleCompany>
    </SingleTestimonial>
  );
};

const SingleTestimonial = styled.div`
  background-color: var(--colour-white-dwarf);
  padding: var(--spacing-x-small) var(--spacing-small);
  position: relative;
  width: 98%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  &:first-of-type {
    border-top-right-radius: var(--spacing-xx-small);
    border-top-left-radius: var(--spacing-xx-small);
  }

  ${screen.md} {
    padding: var(--spacing-small) var(--spacing-small);
    border-radius: var(--spacing-xx-small);
    &:not(:first-of-type) {
      &::before {
        display: none;
      }
    }
  }

  &:not(:first-of-type) {
    border-bottom-right-radius: var(--spacing-xx-small);
    border-bottom-left-radius: var(--spacing-xx-small);
    &::before {
      content: '';
      width: calc(100% - var(--spacing-400));
      height: 1px;
      background-color: var(--colour-deep-space-disabled);
      position: absolute;
      top: 1px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const Audience = styled(Paragraph)`
  font-weight: var(--font-weight-heavy);
  margin-bottom: var(--spacing-xxx-small);
  color: var(--text-strong);
  text-transform: capitalize;
`;

const Quote = styled(Paragraph)`
  margin: var(--spacing-x-small) 0;
  flex-grow: 1;
`;

const Author = styled(Paragraph)`
  font-weight: var(--font-weight-heavy);
  margin-bottom: var(--spacing-xxx-small);
`;

const RoleCompany = styled(Paragraph)`
  color: var(--text-strong);
`;

const LogoWrapper = styled.div`
  margin-top: var(--spacing-xxx-small);
  > img {
    margin-left: 0 !important;
    min-width: unset !important;
    width: 100%;
    max-width: 120px;
    max-height: 55px;
    overflow: hidden;
  }
`;

export { TestimonialCard };
